import { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-cropper'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import 'cropperjs/dist/cropper.css'

import Btn from '../Btn'
import useDialogDismiss from '../../shared/useDialogDismiss'

import styles from './style.module.scss'

/*
 * Breite des gespeicherten Bildes. Ein Profilbild wird nirgends groesser als
 * 160 Pixel gezeigt; 512 laesst Luft fuer Bildschirme mit doppelter Punktdichte
 * und haelt die Datei klein. Ein Foto aus dem Telefon bringt sonst mehrere
 * Megabyte mit, von denen nie etwas zu sehen ist.
 *
 * Die Hoehe ergibt sich aus dem Seitenverhaeltnis: quadratisch beim Profilbild,
 * 16:9 beim Titelbild einer Nachricht.
 */
const KANTE = 512

/** Schrittweite eines Klicks auf Groesser/Kleiner. */
const ZOOM_SCHRITT = 0.1

const ZOOM_MIN = 0
const ZOOM_MAX = 3

function DrehIcon({ nachLinks }) {
	return (
		<svg
			className={styles.icon}
			viewBox="0 0 24 24"
			aria-hidden="true"
			style={nachLinks ? { transform: 'scaleX(-1)' } : undefined}
		>
			<path d="M20 8a9 9 0 1 0 1 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path
				d="M21 3v6h-6"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

DrehIcon.propTypes = { nachLinks: PropTypes.bool }

/**
 * Ein Bild zuschneiden, vergroessern und drehen, bevor es hochgeladen wird.
 *
 * Warum ueberhaupt: Hochgeladen wurde bisher genau die gewaehlte Datei. Ein
 * Foto im Querformat wurde dadurch ueberall zum Streifen, ein hochkant
 * aufgenommenes Bild lag auf der Seite, und wer nur sein Gesicht zeigen wollte,
 * musste die Datei vorher selbst zurechtschneiden.
 *
 * Der Ausschnitt ist fest quadratisch und rund dargestellt - so, wie das Bild
 * spaeter in Listen und Kommentaren erscheint. Bewegt und vergroessert wird das
 * Bild dahinter, nicht der Rahmen: Es gibt damit kein Ergebnis, das im runden
 * Rahmen anders aussieht als in der Vorschau.
 *
 * Herausgegeben wird eine Datei mit ordentlichem Namen samt Endung. Der Server
 * bildet aus ihr den Speichernamen — ein erfundener Name wie "image/jpeg" ergab
 * dort eine Endung, die keine ist.
 */
export default function AvatarEditor({
	file,
	onCancel,
	onDone,
	title,
	round = true,
	aspect = 1,
	breite = KANTE,
	dateiname = 'profilbild.jpg',
	hint
}) {
	const { t } = useTranslation()
	const cropperRef = useRef(null)
	const [src, setSrc] = useState('')
	const [zoom, setZoom] = useState(0)
	const [istBereit, setBereit] = useState(false)
	const [arbeitet, setArbeitet] = useState(false)

	const backdropProps = useDialogDismiss(onCancel)

	/*
	 * Die lokale Adresse muss wieder freigegeben werden. Ohne das behaelt der
	 * Browser jedes einmal geoeffnete Bild bis zum Neuladen der Seite.
	 */
	useEffect(() => {
		if (!file) return undefined

		const url = URL.createObjectURL(file)
		setSrc(url)
		zoomRef.current = 0
		setZoom(0)

		return () => URL.revokeObjectURL(url)
	}, [file])

	const cropper = () => cropperRef.current?.cropper

	/*
	 * Der Regler zeigt einen Stand an, cropper.js kennt aber nur "verstelle um
	 * diesen Betrag" — deshalb die Differenz zum letzten Stand. Der letzte Stand
	 * liegt in einem `ref` und nicht im Zustandswert: Ein Seiteneffekt im
	 * Zustands-Aktualisierer wuerde bei jeder doppelten Auswertung ein zweites
	 * Mal zoomen.
	 */
	const zoomRef = useRef(0)

	const setzeZoom = useCallback(wert => {
		const begrenzt = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, wert))

		cropperRef.current?.cropper?.zoom(begrenzt - zoomRef.current)
		zoomRef.current = begrenzt
		setZoom(begrenzt)
	}, [])

	// Nach dem Drehen passt cropper.js das Bild neu ein — der Regler stuende
	// sonst auf einem Stand, den es nicht mehr gibt.
	const drehe = grad => {
		cropper()?.rotate(grad)
		zoomRef.current = 0
		setZoom(0)
	}

	const zuruecksetzen = () => {
		cropper()?.reset()
		zoomRef.current = 0
		setZoom(0)
	}

	const uebernehmen = () => {
		const aktiv = cropper()
		if (!aktiv || arbeitet) return

		setArbeitet(true)

		/*
		 * Weisser Grund statt durchsichtig: Gespeichert wird JPEG, und dort gibt
		 * es keine Durchsichtigkeit — ohne Fuellfarbe wuerde sie schwarz.
		 */
		const canvas = aktiv.getCroppedCanvas({
			width: breite,
			height: Math.round(breite / aspect),
			imageSmoothingQuality: 'high',
			fillColor: '#fff'
		})

		if (!canvas) {
			setArbeitet(false)
			return
		}

		canvas.toBlob(
			blob => {
				setArbeitet(false)
				if (!blob) return

				onDone(new File([blob], dateiname, { type: 'image/jpeg', lastModified: Date.now() }))
			},
			'image/jpeg',
			0.9
		)
	}

	return (
		<div className={styles.overlay} {...backdropProps}>
			<div className={styles.modal} role="dialog" aria-modal="true" aria-label={title || t('editPicture')}>
				<div className={styles.header}>
					<h2 className={styles.title}>{title || t('editPicture')}</h2>
					<button type="button" className={styles.close} onClick={onCancel} aria-label={t('Close')}>
						×
					</button>
				</div>

				<p className={styles.hint}>{hint || t('avatarEditorHint')}</p>

				<div className={styles.stage}>
					{src && (
						<Cropper
							ref={cropperRef}
							src={src}
							className={round ? styles.round : undefined}
							style={{ height: '100%', width: '100%' }}
							aspectRatio={aspect}
							viewMode={1}
							dragMode="move"
							autoCropArea={1}
							guides={false}
							center={false}
							highlight={false}
							background={true}
							responsive={true}
							// Fester Rahmen, bewegtes Bild: Wer den Rahmen selbst
							// ziehen kann, trifft das Quadrat nur zufaellig.
							cropBoxMovable={false}
							cropBoxResizable={false}
							toggleDragModeOnDblclick={false}
							// Beim Drehen sonst Hochformat statt Querformat,
							// weil cropper.js die Kameraangabe ein zweites Mal
							// anwendet: https://github.com/fengyuanchen/cropperjs/issues/671
							checkOrientation={false}
							ready={() => setBereit(true)}
						/>
					)}
				</div>

				<div className={styles.controls}>
					<div className={styles.zoomRow}>
						<button
							type="button"
							className={styles.stepBtn}
							onClick={() => setzeZoom(zoom - ZOOM_SCHRITT)}
							disabled={!istBereit}
							aria-label={t('zoomOut')}
						>
							−
						</button>
						<input
							type="range"
							className={styles.slider}
							min={ZOOM_MIN}
							max={ZOOM_MAX}
							step="0.01"
							value={zoom}
							disabled={!istBereit}
							aria-label={t('zoom')}
							onChange={event => setzeZoom(Number(event.target.value))}
						/>
						<button
							type="button"
							className={styles.stepBtn}
							onClick={() => setzeZoom(zoom + ZOOM_SCHRITT)}
							disabled={!istBereit}
							aria-label={t('zoomIn')}
						>
							+
						</button>
					</div>

					<div className={styles.toolRow}>
						<button
							type="button"
							className={styles.toolBtn}
							onClick={() => drehe(-90)}
							disabled={!istBereit}
						>
							<DrehIcon nachLinks />
							{t('rotateLeft')}
						</button>
						<button
							type="button"
							className={styles.toolBtn}
							onClick={() => drehe(90)}
							disabled={!istBereit}
						>
							<DrehIcon />
							{t('rotateRight')}
						</button>
						<button type="button" className={styles.toolBtn} onClick={zuruecksetzen} disabled={!istBereit}>
							{t('resetImage')}
						</button>
					</div>
				</div>

				<div className={styles.footer}>
					<Btn type="button" theme="secondary" onClick={onCancel}>
						{t('Cancel')}
					</Btn>
					<Btn type="button" theme="primary" loading={arbeitet} disabled={!istBereit} onClick={uebernehmen}>
						{t('applyPicture')}
					</Btn>
				</div>
			</div>
		</div>
	)
}

AvatarEditor.propTypes = {
	/** Die gewaehlte Datei. Ohne sie zeigt das Fenster eine leere Flaeche. */
	file: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	/** Bekommt die fertig zugeschnittene Datei. */
	onDone: PropTypes.func.isRequired,
	title: PropTypes.string,
	/** Runder Ausschnitt. Für Firmenlogos eckig. */
	round: PropTypes.bool,
	/** Seitenverhältnis des Ausschnitts: 1 = quadratisch, 16/9 = Titelbild. */
	aspect: PropTypes.number,
	/** Breite des gespeicherten Bildes; die Höhe folgt aus `aspect`. */
	breite: PropTypes.number,
	/** Name der herausgegebenen Datei — der Server bildet daraus die Endung. */
	dateiname: PropTypes.string,
	/** Ersetzt den Satz über der Fläche, wo er nicht vom Profilbild handelt. */
	hint: PropTypes.string
}
